import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Search, Filter as FilterIcon } from "lucide-react";

import { getECGReports } from "../alivecor_reports/ecg_vendorapireports";
import { useDebouncedValue } from "../alivecor_reports/ecg_reports_utils";
import ECGReportsFilters from "../alivecor_reports/ecg_vendors_filters";
import ECGReportsTable from "../alivecor_reports/ecg_vendors_table";
import ECGReportsPagination from "../alivecor_reports/ecg_vendors_paginations";

const PAGE_SIZE = 20;

const EMPTY_FILTERS = { customer: "", test_date: "", report_date: "" };

export default function ECGReports() {
  const navigate = useNavigate();

  // Search (server-side, debounced) — separate from the Customer/Date filters.
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);

  // Applied filters — Customer / Test Date / Report Date only. No Location.
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [page, setPage] = useState(1);

  const [reports, setReports] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [stats, setStats] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedReports, setSelectedReports] = useState(new Set());
  const [filterOpen, setFilterOpen] = useState(false);

  const activeFilterCount = ["customer", "test_date", "report_date"].filter(
    (key) => filters[key]
  ).length;
  const hasActiveFilters = activeFilterCount > 0;

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getECGReports(
        {
          search: debouncedSearch,
          customer: filters.customer,
          test_date: filters.test_date,
          report_date: filters.report_date,
        },
        page,
        PAGE_SIZE
      );

      setReports(data.reports || []);
      setCustomers(data.customers || []);
      setPagination(data.pagination || null);
      setStats(data.stats || null);
    } catch (err) {
      console.error("Failed to load ECG reports:", err);
      setError(err);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters, page]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Any change to search or filters resets pagination to page 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters]);

  // Selection is page-aware: clear it whenever the underlying result set changes.
  useEffect(() => {
    setSelectedReports(new Set());
  }, [debouncedSearch, filters, page]);

  const handleApplyFilters = (nextFilters) => {
    setFilters(nextFilters);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
  };

  const handleToggleSelect = (id, checked) => {
    setSelectedReports((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleToggleSelectAll = (checked, pageIds) => {
    setSelectedReports((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const handleView = (report) => {
    if (!report.pdf_url) return;
    window.open(report.pdf_url, "_blank", "noopener,noreferrer");
  };

  const handleDownload = (report) => {
    if (!report.pdf_url) return;
    // S3 signed URLs generally allow direct download; fall back to a new
    // tab if the browser blocks the programmatic download.
    try {
      const link = document.createElement("a");
      link.href = report.pdf_url;
      link.download = "";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Direct download failed, opening in new tab instead:", err);
      window.open(report.pdf_url, "_blank", "noopener,noreferrer");
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const totalReports = stats?.total_reports ?? 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5
                       text-sm font-medium text-white hover:bg-red-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <FileText className="h-5 w-5 text-gray-400" />

          <div>
            <h1 className="text-lg font-semibold text-gray-900">ECG Reports</h1>
            <p className="text-sm text-gray-500">Total Reports: {totalReports}</p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {/* Search + Filter bar */}
        <div className="relative mb-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by patient name or ID..."
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm
                         text-gray-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            />
          </div>

          <button
            type="button"
            onClick={() => setFilterOpen((open) => !open)}
            className="relative inline-flex items-center gap-1.5 rounded-lg border border-gray-300
                       bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <FilterIcon className="h-4 w-4" />
            Filter
            {activeFilterCount > 0 && (
              <span className="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1 text-xs font-semibold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>

          <ECGReportsFilters
            isOpen={filterOpen}
            onClose={() => setFilterOpen(false)}
            customers={customers}
            appliedFilters={filters}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            activeFilterCount={activeFilterCount}
          />
        </div>

        {/* Table */}
        <ECGReportsTable
          reports={reports}
          loading={loading}
          error={error}
          onRetry={fetchReports}
          selectedReports={selectedReports}
          onToggleSelect={handleToggleSelect}
          onToggleSelectAll={handleToggleSelectAll}
          onView={handleView}
          onDownload={handleDownload}
          hasActiveFilters={hasActiveFilters || Boolean(debouncedSearch)}
          onClearFilters={() => {
            handleClearFilters();
            setSearch("");
          }}
        />

        {/* Pagination */}
        {!loading && !error && (
          <ECGReportsPagination pagination={pagination} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}